import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { GalleryImage } from "@/components/feature/GalleryGrid";

interface ArticlePhotoStripProps {
  photos: GalleryImage[];
}

/** Kleine, klickbare Bildzeile unter einem Presseartikel (öffnet Vollbild). */
export default function ArticlePhotoStrip({ photos }: ArticlePhotoStripProps) {
  const { t, i18n } = useTranslation();
  const isDe = (i18n.language || "de").startsWith("de");
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const close = useCallback(() => setOpenIndex(null), []);

  const step = useCallback(
    (direction: number) => {
      setOpenIndex((prev) => {
        if (prev === null) return prev;
        return (prev + direction + photos.length) % photos.length;
      });
    },
    [photos.length],
  );

  useEffect(() => {
    if (openIndex === null) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
      if (event.key === "ArrowLeft") step(-1);
      if (event.key === "ArrowRight") step(1);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [openIndex, close, step]);

  if (photos.length === 0) return null;

  const captionOf = (photo: GalleryImage) =>
    isDe ? photo.caption : photo.captionEn;

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        {photos.map((photo, position) => (
          <button
            key={photo.key}
            type="button"
            onClick={() => setOpenIndex(position)}
            aria-label={captionOf(photo)}
            className="group relative w-16 h-16 md:w-20 md:h-20 rounded-md overflow-hidden border border-background-200 bg-background-200 cursor-pointer"
          >
            <img
              src={photo.thumb}
              alt={captionOf(photo)}
              title={captionOf(photo)}
              className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            />
            <span className="absolute inset-0 bg-foreground-950/0 group-hover:bg-foreground-950/30 transition-colors flex items-center justify-center">
              <i className="ri-zoom-in-line text-background-50 text-base opacity-0 group-hover:opacity-100 transition-opacity"></i>
            </span>
          </button>
        ))}
      </div>

      {openIndex !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={close}
        >
          <button
            type="button"
            onClick={close}
            aria-label={t("nost_press_2_close")}
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-red-600 text-white hover:bg-red-700 shadow-lg cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-xl"></i>
          </button>

          {photos.length > 1 && (
            <>
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  step(-1);
                }}
                aria-label="Vorheriges Bild"
                className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 cursor-pointer transition-colors"
              >
                <i className="ri-arrow-left-line text-xl"></i>
              </button>
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  step(1);
                }}
                aria-label="Nächstes Bild"
                className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 cursor-pointer transition-colors"
              >
                <i className="ri-arrow-right-line text-xl"></i>
              </button>
            </>
          )}

          <div
            className="max-w-4xl max-h-[85vh]"
            onClick={(event) => event.stopPropagation()}
          >
            <img
              src={photos[openIndex].full}
              alt={captionOf(photos[openIndex])}
              className="max-w-full max-h-[80vh] object-contain rounded-lg bg-white"
            />
            <p className="text-white text-sm text-center mt-3">
              {captionOf(photos[openIndex])}
              {photos.length > 1 && (
                <span className="text-white/60 ml-2">
                  ({openIndex + 1} / {photos.length})
                </span>
              )}
            </p>
          </div>
        </div>
      )}
    </>
  );
}